import { useState } from "react";
import { ChannelEnabledToggle } from "../../atoms/ChannelEnabledToggle";
import { SecretInput } from "../../atoms/SecretInput";
import { TestSendPanel } from "../../molecules/TestSendPanel";
import type { ChannelSettingResponse, EmailConfig } from "../../../types/subscriptionSettings";
import type { UseChannelSettings } from "../../../hooks/useChannelSettings";

interface Props {
  ctrl: UseChannelSettings;
  setting: ChannelSettingResponse;
}

export function EmailChannelTab({ ctrl, setting }: Props) {
  const initial = setting.config as EmailConfig;
  const [enabled, setEnabled] = useState(setting.is_enabled);
  const [sendgridApiKey, setSendgridApiKey] = useState("");
  const [fromAddress, setFromAddress] = useState(initial.from_address ?? "");
  const [fromName, setFromName] = useState(initial.from_name ?? "");
  const [replyTo, setReplyTo] = useState(initial.reply_to ?? "");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const errors = ctrl.fieldErrors.email;
  const apiKeyIsSet = initial.sendgrid_api_key?.is_set ?? false;

  async function handleSave() {
    setSaveStatus("saving");
    const config: Record<string, unknown> = {
      from_address: fromAddress,
    };
    if (fromName) config.from_name = fromName;
    if (replyTo) config.reply_to = replyTo;
    if (sendgridApiKey) config.sendgrid_api_key = sendgridApiKey;
    const result = await ctrl.save("email", { is_enabled: enabled, config });
    setSaveStatus(result ? "saved" : "error");
    if (result) setSendgridApiKey("");
  }

  return (
    <div className="ac-channel-tab">
      <div className="ac-channel-tab__header">
        <h2 className="ac-channel-tab__title">Email (SendGrid)</h2>
        <ChannelEnabledToggle enabled={enabled} onChange={setEnabled} />
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="email-apikey">SendGrid API key</label>
        <SecretInput id="email-apikey" value={sendgridApiKey} onChange={setSendgridApiKey} isSet={apiKeyIsSet} ariaLabel="SendGrid API key" />
        {errors.sendgrid_api_key && <span className="ac-form-row__error">{errors.sendgrid_api_key.join(" ")}</span>}
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="email-from">From address</label>
        <input id="email-from" className="ac-input" value={fromAddress} onChange={(e) => setFromAddress(e.target.value)} placeholder="noreply@example.com" />
        {errors.from_address && <span className="ac-form-row__error">{errors.from_address.join(" ")}</span>}
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="email-from-name">From name (optional)</label>
        <input id="email-from-name" className="ac-input" value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Pulseboard Notifications" />
        {errors.from_name && <span className="ac-form-row__error">{errors.from_name.join(" ")}</span>}
      </div>

      <div className="ac-form-row">
        <label className="ac-form-row__label" htmlFor="email-reply">Reply-to (optional)</label>
        <input id="email-reply" className="ac-input" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} />
        {errors.reply_to && <span className="ac-form-row__error">{errors.reply_to.join(" ")}</span>}
      </div>

      <div className="ac-button-row">
        <button type="button" className="ac-button" onClick={handleSave} disabled={saveStatus === "saving"}>
          {saveStatus === "saving" ? "Saving..." : "Save"}
        </button>
        {saveStatus === "saved" && <span className="ac-alert ac-alert--success" role="status">Saved.</span>}
        {saveStatus === "error" && <span className="ac-alert ac-alert--error" role="status">Failed to save.</span>}
      </div>

      <TestSendPanel
        destinationLabel="Send test email to"
        placeholder="you@example.com"
        disabled={!enabled}
        onSend={(dest) => ctrl.test("email", dest)}
      />
    </div>
  );
}
